import { lazy } from 'react';
import { Navigate, RouteObject } from 'react-router-dom';

import Home from '../pages/Home';
import Login from '../pages/Login';
import withPrivateAccess from '../hocs/withPrivateAccess';
import withPublicAccess from '../hocs/withPublicAccess';
import withAsyncSatAccess from '../hocs/withAsyncSatAccess';
import { ROUTES } from './routes';
import { qrUploadLoader } from './loaders/qrUploadLoader';

const HomeWithPrivateAccess = withPrivateAccess(Home);
const LoginWithPublicAccess = withPublicAccess(Login);

const LazyAssignments = lazy(() => import('../pages/Assignments'));
const LazyDashboard = lazy(() => import('../pages/Dashboard'));
const LazyLiveClasses = lazy(() => import('../pages/LiveClasses'));
const LazyLiveClassDetails = lazy(() => import('../pages/LiveClassDetails'));
const LazyAiTutor = lazy(() => import('../pages/AiTutor_cs/index_cs'));
const LazyAiTutorConceptSessionGenerate = lazy(() => import('../pages/AiTutor_cs/AiTutorConceptSessionGenerate'));
const LazySparky = lazy(() => import('../pages/Sparky'));
const LazySparkySessions = lazy(() => import('../pages/SparkySesssions'));
const LazyLiveTests = lazy(() => import('../pages/LiveTests'));
const LazyPractice = lazy(() => import('../pages/Practice'));
const LazyPracticeFlow = lazy(() => import('../pages/Practice/PracticeFlow'));
const LazyLearningZone = lazy(() => import('../pages/LearningZone'));
const LazySso = lazy(() => import('../pages/Sso/Sso'));
const LazyQrUpload = lazy(() => import('../pages/QrUpload/QrUploadPage'));
const LazyVideoPresentation = lazy(() => import('../pages/VideoPresentation'));
const LazySatDashboard = lazy(() => import('../pages/SatDashboard'));
const LazyTestReportPage = lazy(() => import('../pages/SatDashboard/containers/TestReportPage'));

// The practice flow is a full-screen experience, so it renders outside the Home
// layout instead of behind it as an overlay.
const PracticeWithPrivateAccess = withPrivateAccess(() => <LazyPracticeFlow />);

const LearningZoneWithAsyncSatAccess = withAsyncSatAccess(() => <LazyLearningZone />);
const VideoPresentationWithAsyncSatAccess = withAsyncSatAccess(() => <LazyVideoPresentation />);
const SatDashboardWithAsyncSatAccess = withAsyncSatAccess(() => <LazySatDashboard />);
const TestReportPageWithAsyncSatAccess = withAsyncSatAccess(() => <LazyTestReportPage />);
const PracticeWithAsyncSatAccess = withAsyncSatAccess(() => <LazyPractice />);

const routesArr: RouteObject[] = [
  {
    path: ROUTES.LOGIN,
    element: <LoginWithPublicAccess />,
  },
  {
    path: ROUTES.SSO,
    element: <LazySso />,
  },
  // QR Upload route - standalone page (not inside Home layout, like sparkl-web)
  {
    path: ROUTES.TEST_QR_UPLOAD,
    loader: qrUploadLoader,
    element: <LazyQrUpload />,
  },
  {
    path: ROUTES.PRACTICE_LEVEL,
    element: <PracticeWithPrivateAccess />,
  },
  {
    path: '',
    element: <HomeWithPrivateAccess />,
    children: [
      {
        path: '',
        element: <Navigate to={ROUTES.DASHBOARD} />,
      },
      {
        path: ROUTES.DASHBOARD,
        element: <LazyDashboard />,
      },
      {
        path: ROUTES.CLASSES,
        element: <LazyLiveClasses />,
      },
      {
        path: ROUTES.CLASS_DETAILS,
        element: <LazyLiveClassDetails />,
      },

      {
        path: ROUTES.ASSIGNMENTS,
        element: <LazyAssignments />,
      },
      {
        path: ROUTES.AI_TUTOR,
        element: <LazyAiTutor />,
      },
      {
        path: ROUTES.AI_TUTOR_CONCEPT_SESSION_GENERATE,
        element: <LazyAiTutorConceptSessionGenerate />,
      },
      {
        path: ROUTES.SPARKY,
        element: <LazySparky />,
      },
      {
        path: ROUTES.SPARKY_SESSIONS,
        element: <LazySparkySessions />,
      },
      {
        path: ROUTES.TESTS,
        element: <LazyLiveTests />,
      },
      {
        path: ROUTES.PRACTICE,
        element: <PracticeWithAsyncSatAccess />,
      },
      {
        path: ROUTES.LEARNING_ZONE,
        element: <LearningZoneWithAsyncSatAccess />,
      },
      {
        path: ROUTES.VIDEO_PRESENTATION,
        element: <VideoPresentationWithAsyncSatAccess />,
      },
      {
        path: ROUTES.SAT_DASHBOARD,
        element: <SatDashboardWithAsyncSatAccess />,
      },
      {
        path: ROUTES.SAT_TEST_REPORT,
        element: <TestReportPageWithAsyncSatAccess />,
      },
      // {
      //   path: ROUTES.REPORTS,
      //   element: <div style={{ margin: '2rem' }}>Reports</div>,
      // },
      // {
      //   path: ROUTES.SKILLS,
      //   element: <div style={{ margin: '2rem' }}>Skills</div>,
      // },
      {
        path: ROUTES.DEFAULT,
        element: <Navigate to={ROUTES.DASHBOARD} />,
      },
    ],
  },
];

export default routesArr;
